<script lang="ts">
  import { onMount } from 'svelte';
  import { game } from '$stores/game.svelte';
  import { theme } from '$stores/theme.svelte';
  import { pair } from '$stores/pair.svelte';
  import { profile } from '$stores/profile.svelte';
  import { twinsDrawer } from '$stores/twinsDrawer.svelte';
  import { identity } from '$crypto/identity.svelte';
  import {
    startInboxPolling,
    stopInboxPolling,
    pollInbox
  } from '$game/bond-inbox';
  import { vuAudio } from '$lib/audio/vuAudio.svelte';
  import { loadPrefs, savePrefs } from '$lib/audio/audioPreferences';
  import BondCelebration from '$components/BondCelebration.svelte';
  import Splash from '$components/Splash.svelte';
  import Intro from '$components/Intro.svelte';
  import Play from '$components/Play.svelte';
  import Wait from '$components/Wait.svelte';
  import Reveal from '$components/Reveal.svelte';
  import Share from '$components/Share.svelte';
  import Scoreboard from '$components/Scoreboard.svelte';
  import Settings from '$components/Settings.svelte';
  import TwinsDrawer from '$components/TwinsDrawer.svelte';
  import Mascot from '$components/Mascot.svelte';

  let splashRef: { play: () => void } | undefined = $state();

  function openScoreboard() {
    if (game.screen !== 'scoreboard' && game.screen !== 'settings') {
      game.scoreboardOrigin = game.screen;
    } else if (game.screen === 'scoreboard') {
      game.scoreboardOrigin = 'intro';
    }
    game.goto('scoreboard');
  }

  function openTwins() {
    twinsDrawer.openDrawer();
  }

  function openSettings() {
    if (game.screen !== 'settings' && game.screen !== 'scoreboard') {
      game.scoreboardOrigin = game.screen;
    }
    game.goto('settings');
  }

  /** Brand click → home (intro). Also dismisses the twins drawer if open
   *  so a tap on the logo always returns the user to a clean Intro view. */
  function goHome() {
    if (twinsDrawer.open) twinsDrawer.close();
    if (game.screen !== 'intro') game.goto('intro');
  }

  /** Replay is now driven by the Settings "Reset everything" action. The
   *  Settings component fires a `vutwin:replay` event so it doesn't need a
   *  reference to the splash component. */
  function onReplayRequested() {
    splashRef?.play();
  }

  /** Full-screen celebration overlay (replaces the prior tiny toast). */
  let celebrationOpen = $state(false);
  let celebrationHandle = $state('twin');

  function onBondCelebration(e: Event) {
    const detail = (e as CustomEvent<{ handle?: string }>).detail;
    const handle = detail?.handle?.trim() || 'twin';
    celebrationHandle = handle;
    // Re-trigger on rapid back-to-back bonds by closing first if already open.
    if (celebrationOpen) celebrationOpen = false;
    queueMicrotask(() => (celebrationOpen = true));
  }

  /** Re-poll the bond inbox whenever the tab becomes visible — covers
   *  the "phone was locked while my friend bonded" case without spinning
   *  a fast timer. */
  function onVisibilityChange() {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      void pollInbox();
    }
  }

  /** One-shot Web Audio unlock primer.
   *
   *  iOS / Safari refuse to start a fresh AudioContext outside a user
   *  gesture. The auto-bond receiver path fires from a polling timer,
   *  which is NOT a gesture — so we attach a one-shot listener on the
   *  very first tap/key and initialise the engine then. After that, the
   *  context is unlocked for the rest of the session and any
   *  bond-celebration event (initiator OR receiver) can play sound. */
  function onFirstGesture() {
    void vuAudio.init();
    window.removeEventListener('pointerdown', onFirstGesture);
    window.removeEventListener('keydown', onFirstGesture);
    window.removeEventListener('touchstart', onFirstGesture);
  }

  /** Header mute toggle handler — clicks count as user gestures so it's
   *  safe to lazily init() the engine here. */
  async function toggleMute() {
    if (!vuAudio.ready) await vuAudio.init();
    vuAudio.toggleMute();
    savePrefs();
  }

  const FIRST_LAUNCH_KEY = 'vutwin.firstLaunchSeen';

  onMount(() => {
    theme.set(theme.current);
    identity.ensureLoaded();
    pair.hydrate();
    profile.hydrate();

    window.addEventListener('vutwin:replay', onReplayRequested);
    window.addEventListener('vutwin:bondcelebration', onBondCelebration as EventListener);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Hydrate persisted audio prefs (mute / volume) before any sound plays.
    loadPrefs();

    // Arm the iOS / Safari audio unlock on the first user interaction.
    window.addEventListener('pointerdown', onFirstGesture, { once: false });
    window.addEventListener('keydown', onFirstGesture, { once: false });
    window.addEventListener('touchstart', onFirstGesture, { once: false });

    // Begin background polling for incoming bond handshakes. Idempotent.
    startInboxPolling();

    // Auto-open the twins drawer when arriving via a `?bond=<pubkey>&h=<handle>`
    // link, with the friend's bond code prefilled into the inline add card.
    const url = new URL(window.location.href);
    const bondParam = url.searchParams.get('bond');
    const handleParam = url.searchParams.get('h') ?? '';
    const cleanup = () => {
      window.removeEventListener('vutwin:replay', onReplayRequested);
      window.removeEventListener(
        'vutwin:bondcelebration',
        onBondCelebration as EventListener
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
      window.removeEventListener('touchstart', onFirstGesture);
      stopInboxPolling();
    };

    if (bondParam) {
      twinsDrawer.openDrawer({ code: bondParam, handle: handleParam });
      return cleanup;
    }

    // First-ever launch on this device with no twins → open the twins drawer
    // so the user sees their bond code + invite share-sheet ready to go.
    const seen = localStorage.getItem(FIRST_LAUNCH_KEY) === '1';
    if (!seen && pair.active.length === 0) {
      localStorage.setItem(FIRST_LAUNCH_KEY, '1');
      twinsDrawer.openDrawer();
    }

    return cleanup;
  });
</script>

<Splash bind:this={splashRef} />

<div id="app">
  <header class="header">
    <button
      type="button"
      class="brand"
      aria-label="Go to home"
      onclick={goHome}
    >
      <span class="brand-mark"><Mascot size={28} state="idle" /></span>
      <span class="brand-text">VuTwin<span class="brand-tag"> · DAILY</span></span>
    </button>
    <div class="header-actions">
      <button
        class="icon-btn icon-btn-sound icon-btn-mute"
        aria-label={vuAudio.muted ? 'Unmute bond sound' : 'Mute bond sound'}
        aria-pressed={vuAudio.muted}
        onclick={toggleMute}
      >
        {#if vuAudio.muted}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 5L6 9H2v6h4l5 4V5z" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M23 9l-6 6M17 9l6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        {:else}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 5L6 9H2v6h4l5 4V5z" stroke-linecap="round" stroke-linejoin="round" />
            <path
              d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        {/if}
      </button>
      <button class="icon-btn icon-btn-scoreboard" aria-label="Open scoreboard" onclick={openScoreboard}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 21h12M9 21V9h6v12M5 21V13h4M19 21v-5h-4" />
        </svg>
      </button>
      <button class="icon-btn icon-btn-twins" aria-label="Open twins" onclick={openTwins}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="9" cy="8" r="3.5" />
          <circle cx="16" cy="9" r="2.5" />
          <path d="M3 20c0-3 2.5-5 6-5s6 2 6 5" />
          <path d="M14 20c.4-2 2-3.5 4.5-3.5" />
        </svg>
        {#if pair.isBonded}<span class="dot-bonded"></span>{/if}
      </button>
      <button class="icon-btn icon-btn-settings" aria-label="Open settings" onclick={openSettings}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      </button>
    </div>
  </header>

  {#if game.screen !== 'scoreboard' && game.screen !== 'settings' && game.screen !== 'intro' && pair.isBonded}
    <div class="streak-bar">
      <div class="streak-info">
        <div class="friend-avatar">{pair.friendHandle.slice(0, 1).toUpperCase()}</div>
        <div class="streak-text">
          with <strong>@{pair.friendHandle}</strong>
          <span class="streak-flame">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2s4 4 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3s-3 2-3 6a6 6 0 0 0 12 0c0-7-6-12-6-12z" />
            </svg>
            {game.streak}
          </span>
        </div>
      </div>
      {#if game.pulse}
        <div class="day-badge">PULSE {game.pulse.id}</div>
      {/if}
    </div>
  {/if}

  <main class="stage">
    {#if game.screen === 'intro'}
      <Intro />
    {:else if game.screen === 'play'}
      <Play />
    {:else if game.screen === 'wait'}
      <Wait />
    {:else if game.screen === 'reveal'}
      <Reveal />
    {:else if game.screen === 'share'}
      <Share />
    {:else if game.screen === 'scoreboard'}
      <Scoreboard />
    {:else if game.screen === 'settings'}
      <Settings />
    {/if}
  </main>
</div>

<TwinsDrawer />

<BondCelebration bind:show={celebrationOpen} friendName={celebrationHandle} />

<style>
  #app {
    width: 100%;
    inline-size: min(100%, 480px);
    height: 100vh;
    height: 100svh;
    height: 100dvh;
    margin: 0 auto;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    /* IMPORTANT: top/bottom safe-areas are owned by the children
       (.header / .streak-bar / each screen's CTA wrap) so we don't
       double-pad against the Dynamic Island and home indicator on
       iPhone 15/16 Pro. Only side insets are handled here for
       landscape notches. */
    padding-right: var(--safe-right, 0);
    padding-left: var(--safe-left, 0);
  }
  .header {
    flex: 0 0 auto;
    padding: max(var(--safe-top), 12px) var(--gutter) 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 2;
  }
  .brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 800;
    letter-spacing: -0.02em;
    font-size: 18px;
    /* Button reset — keep the visual identity, lose the native chrome. */
    padding: 4px 6px;
    margin: -4px -6px;
    background: transparent;
    border: 0;
    color: inherit;
    font-family: inherit;
    cursor: pointer;
    border-radius: var(--radius-sm);
    transition: var(--transition);
    -webkit-tap-highlight-color: transparent;
  }
  .brand:hover {
    background: var(--surface);
  }
  .brand:active {
    transform: scale(0.96);
  }
  .brand:focus-visible {
    outline: 2px solid var(--cyan);
    outline-offset: 2px;
  }
  :global(body.brutal) .brand {
    text-transform: uppercase;
    border-radius: 0;
  }
  :global(body.brutal) .brand:focus-visible {
    outline-color: var(--text);
  }
  .brand-text {
    display: inline-flex;
    align-items: baseline;
  }
  .brand-mark {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .brand-tag {
    font-size: 10px;
    color: var(--text-3);
    margin-left: 4px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  /* ============ Gamified header chips ============
     Each chip reads as a position-locked HUD tile. Glossy highlight,
     per-button gradient, depth shadow that collapses on press. Mirrors
     the .shape-tile vocabulary so the brand reads as one coherent toy. */
  .icon-btn {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: var(--r-md);
    border: 2px solid rgba(255, 255, 255, 0.18);
    display: grid;
    place-items: center;
    cursor: pointer;
    overflow: hidden;
    background: linear-gradient(135deg, var(--surface) 0%, var(--surface-2) 100%);
    box-shadow:
      var(--depth-sm) rgba(0, 0, 0, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
    transition:
      transform 120ms var(--spring-bouncy),
      box-shadow 120ms var(--spring-bouncy),
      border-color 160ms ease;
  }
  /* Glossy highlight — same lighting language as .shape-tile / .btn-hero. */
  .icon-btn::before {
    content: '';
    position: absolute;
    top: 6%;
    left: 8%;
    width: 38%;
    height: 30%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.45), transparent);
    border-radius: 8px;
    pointer-events: none;
  }
  .icon-btn svg {
    width: 18px;
    height: 18px;
    stroke: #fff;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.45));
    position: relative;
    z-index: 1;
  }
  .icon-btn:active {
    transform: translateY(2px);
    box-shadow:
      0 0 0 0 rgba(0, 0, 0, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  .icon-btn:hover {
    border-color: rgba(255, 255, 255, 0.32);
  }

  /* Per-position gradients + matching depth shadow + matching glow. */
  .icon-btn-sound {
    background: linear-gradient(135deg, #7feeff 0%, #00d4ff 55%, #4f46e5 100%);
    border-color: rgba(0, 212, 255, 0.55);
    box-shadow:
      var(--depth-md) #1e3a8a,
      0 0 14px rgba(0, 212, 255, 0.35);
  }
  .icon-btn-scoreboard {
    background: linear-gradient(135deg, #fff3a3 0%, var(--gold) 55%, #b8860b 100%);
    border-color: rgba(255, 216, 74, 0.6);
    box-shadow:
      var(--depth-md) var(--gold-deep, #8a6914),
      0 0 14px rgba(255, 216, 74, 0.4);
  }
  .icon-btn-scoreboard svg {
    stroke: #2d1a00;
    filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.4));
  }
  .icon-btn-twins {
    background: linear-gradient(135deg, #ff8acf 0%, var(--hot) 55%, var(--violet) 100%);
    border-color: rgba(255, 56, 188, 0.55);
    box-shadow:
      var(--depth-md) var(--hot-deep, #7a1c54),
      0 0 14px rgba(255, 56, 188, 0.4);
  }
  .icon-btn-settings {
    background: linear-gradient(135deg, #94a3b8 0%, #475569 55%, #1e293b 100%);
    border-color: rgba(148, 163, 184, 0.4);
    box-shadow:
      var(--depth-md) #0f172a,
      0 0 12px rgba(148, 163, 184, 0.25);
  }

  /* :active depth-collapse per-position — keeps the press feel consistent
     with the per-button shadow colour. */
  .icon-btn-sound:active {
    box-shadow:
      0 0 0 0 #1e3a8a,
      0 0 14px rgba(0, 212, 255, 0.35);
  }
  .icon-btn-scoreboard:active {
    box-shadow:
      0 0 0 0 var(--gold-deep, #8a6914),
      0 0 14px rgba(255, 216, 74, 0.4);
  }
  .icon-btn-twins:active {
    box-shadow:
      0 0 0 0 var(--hot-deep, #7a1c54),
      0 0 14px rgba(255, 56, 188, 0.4);
  }
  .icon-btn-settings:active {
    box-shadow:
      0 0 0 0 #0f172a,
      0 0 12px rgba(148, 163, 184, 0.25);
  }

  /* Mute state: desaturate + drop the bloom so the off state reads. */
  .icon-btn-mute[aria-pressed='true'] {
    background: linear-gradient(135deg, #475569 0%, #334155 100%);
    border-color: rgba(148, 163, 184, 0.3);
    box-shadow:
      var(--depth-sm) #0f172a,
      0 0 0 0 transparent;
  }
  .icon-btn-mute[aria-pressed='true'] svg {
    stroke: var(--text-3);
    opacity: 0.7;
  }

  /* Bonded dot upgrade: keep the green pulse but raise it above the gloss
     strip and give it a subtle ring so it reads as an unread notification. */
  .dot-bonded {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent-green);
    box-shadow:
      0 0 0 2px var(--bg-deep),
      0 0 8px var(--accent-green);
    animation: bondedPulse 1.8s ease-in-out infinite;
    z-index: 2;
  }
  @keyframes bondedPulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.25);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .dot-bonded {
      animation: none;
    }
  }

  /* Brutalist override: collapse to monochrome plates with hard offset
     shadow, matching the existing language used elsewhere. */
  :global(body.brutal) .icon-btn {
    background: var(--bg);
    border: 2px solid var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
    border-radius: 0;
  }
  :global(body.brutal) .icon-btn::before {
    display: none;
  }
  :global(body.brutal) .icon-btn svg {
    stroke: var(--text);
    filter: none;
  }
  :global(body.brutal) .icon-btn-scoreboard svg {
    stroke: var(--text);
  }
  :global(body.brutal) .icon-btn:active {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px 0 0 var(--text);
  }
  :global(body.brutal) .dot-bonded {
    border-radius: 0;
    box-shadow: 0 0 0 1px var(--text);
    animation: none;
  }
  .streak-bar {
    flex: 0 0 auto;
    margin: 4px var(--gutter) 10px;
    padding: 8px 12px;
    background: linear-gradient(90deg, rgba(255, 56, 188, 0.18), rgba(0, 220, 255, 0.12));
    border: 1px solid var(--border-warm);
    border-radius: var(--r-md);
    box-shadow: 0 0 18px rgba(255, 56, 188, 0.18);
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 2;
  }
  :global(body.brutal) .streak-bar {
    background: var(--bg);
    border-width: 2px;
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .streak-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .friend-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--hot), var(--violet));
    border: 2px solid rgba(255, 255, 255, 0.25);
    display: grid;
    place-items: center;
    color: #fff;
    font-weight: 900;
    font-size: 13px;
    flex-shrink: 0;
    box-shadow: var(--depth-sm) var(--hot-deep);
  }
  :global(body.brutal) .friend-avatar {
    border-radius: 0;
    background: var(--text);
    color: var(--bg);
    border: 2px solid var(--text);
    box-shadow: 2px 2px 0 0 var(--text);
  }
  .streak-text {
    font-size: 13px;
    color: var(--text-2);
  }
  .streak-text strong {
    color: var(--text);
    font-weight: 800;
  }
  .streak-flame {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--gold);
    font-weight: 900;
    font-size: 13px;
    text-shadow: 0 0 10px rgba(255, 216, 74, 0.6);
  }
  :global(body.brutal) .streak-flame {
    color: var(--text);
    text-shadow: none;
  }
  .streak-flame svg {
    width: 14px;
    height: 14px;
  }
  .day-badge {
    background: rgba(255, 216, 74, 0.18);
    color: var(--gold);
    padding: 4px 10px;
    border-radius: var(--r-pill);
    border: 1px solid rgba(255, 216, 74, 0.4);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  :global(body.brutal) .day-badge {
    background: var(--bg);
    color: var(--text);
    border: 2px solid var(--text);
  }
  .stage {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
    overflow: hidden;
    z-index: 2;
    display: flex;
    flex-direction: column;
    view-transition-name: app-stage;
    contain: layout paint;
  }

</style>
